import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AmenityCategory } from "@postnatal-care/shared-types";
import {
  apiClient,
  del,
  get,
  post,
  postForm,
  put,
} from "../services/apiClient";

export type BusinessStatus = "PENDING" | "APPROVED" | "REJECTED" | "SUSPENDED";

const apiPatch = <T = any>(endpoint: string, body: any): Promise<T> =>
  apiClient<T>(endpoint, { method: "PATCH", body });

export interface BankAccount {
  accountName: string;
  accountNumber: string;
  ifsc: string;
  bankName: string;
  branch?: string;
}

export type DocStatus = "PENDING" | "APPROVED" | "REJECTED";

export interface BusinessDocumentEntity {
  id: string;
  docType: string;
  fileUrl: string;
  label?: string | null;
  docNumber?: string | null;
  issuedAt?: string | null;
  expiryDate?: string | null;
  fileName?: string | null;
  mimeType?: string | null;
  sizeBytes?: number | null;
  verified: boolean;
  viewedAt?: string | null;
  viewedBy?: string | null;
  uploadedAt: string;
  docStatus: DocStatus;
  updatedAt?: string | null;
  updatedBy?: string | null;
  rejectionNote?: string | null;
}

export interface BusinessStaffEntity {
  id: string;
  name: string;
  phone: string;
  role: string;
}

export interface BusinessEntity {
  id: string;
  ownerId: string;
  owner?: {
    id: string;
    name: string;
    phone: string;
    email?: string | null;
  } | null;
  businessName: string;
  nameEn: string;
  nameMl: string;
  descriptionEn?: string | null;
  descriptionMl?: string | null;
  logoUrl?: string | null;
  phone: string;
  email?: string | null;
  whatsapp?: string | null;
  instagramUrl?: string | null;
  facebookUrl?: string | null;
  youtubeUrl?: string | null;
  address: string;
  city: string;
  district?: string | null;
  state?: string | null;
  pincode?: string | null;
  lat?: number | null;
  lng?: number | null;
  careTypes: string[];
  totalBeds?: number | null;
  amenities?: Array<{ amenityId: string; amenity?: AmenityMasterEntity }>;
  status: BusinessStatus | string;
  isVisible: boolean;
  isFeatured: boolean;
  showBranchesInCustomerApp?: boolean;
  avgRating?: number | null;
  reviewCount?: number | null;
  bankAccount?: BankAccount | null;
  upiId?: string | null;
  gstNumber?: string | null;
  panNumber?: string | null;
  tradeLicenseNumber?: string | null;
  fssaiNumber?: string | null;
  shopEstablishmentNumber?: string | null;
  isMedicalProfessional?: boolean;
  medicalRegistrationNo?: string | null;
  medicalCouncil?: string | null;
  medicalQualifications?: string | null;
  rejectionReason?: string | null;
  approvedAt?: string | null;
  approvedBy?: string | null;
  createdAt: string;
  documents?: BusinessDocumentEntity[];
  staff?: BusinessStaffEntity[];
  photos?: Array<{
    id: string;
    url: string;
    isCover: boolean;
    sortOrder: number;
  }>;
  roomTypes?: Array<{ id: string; name: string; pricePerNight: number }>;
  packages?: Array<{ id: string; name: string; basePrice: number }>;
}

export interface BusinessListItem {
  id: string;
  businessName: string;
  nameEn: string;
  logoUrl?: string | null;
  city: string;
  phone: string;
  email?: string | null;
  status: BusinessStatus | string;
  isVisible: boolean;
  isFeatured: boolean;
  avgRating?: number | null;
  reviewCount?: number | null;
  createdAt: string;
  owner: {
    id: string;
    name: string;
    phone: string;
    email?: string | null;
    _count?: { businesses: number };
    subscriptions?: Array<{
      id: string;
      status: string;
      billingCycle?: string | null;
    }>;
  };
  _count?: { documents: number; reviews: number; bookings: number };
}

export interface BusinessListParams {
  search?: string;
  status?: BusinessStatus | string;
  city?: string;
  ownerId?: string;
  isFeatured?: boolean;
  page?: number;
  limit?: number;
}

export interface BusinessListResponse {
  data: BusinessListItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface BusinessMapLocation {
  id: string;
  type: "BUSINESS" | "BRANCH";
  businessId: string;
  businessName: string;
  branchName: string | null;
  isPrimary: boolean;
  businessStatus: BusinessStatus | string;
  isBusinessVisible: boolean;
  branchStatus: "ACTIVE" | "INACTIVE" | null;
  isBranchVisible: boolean | null;
  address: string;
  city: string;
  phone?: string | null;
  email?: string | null;
  whatsapp?: string | null;
  ownerName?: string | null;
  lat: number;
  lng: number;
}

export interface BusinessMapLocationsResponse {
  locations: BusinessMapLocation[];
  missingCoordinates: number;
}

export interface BusinessMapLocationParams {
  search?: string;
  status?: BusinessStatus | string;
  city?: string;
}

export interface ActivityLogEntry {
  id: string;
  action: string;
  entityType: string;
  entityId: string;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  admin: { id: string; name: string | null; email: string | null };
}

export interface ActivityListResponse {
  data: ActivityLogEntry[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const businessKeys = {
  all: ["businesses"] as const,
  list: (params: BusinessListParams) =>
    [...businessKeys.all, "list", params] as const,
  detail: (id: string) => [...businessKeys.all, "detail", id] as const,
  cities: () => [...businessKeys.all, "cities"] as const,
  mapLocations: (params: BusinessMapLocationParams) =>
    [...businessKeys.all, "map-locations", params] as const,
  activity: (id: string) => [...businessKeys.all, "activity", id] as const,
};

export const useBusinessList = (params: BusinessListParams = {}) =>
  useQuery<BusinessListResponse>({
    queryKey: businessKeys.list(params),
    queryFn: () => get<BusinessListResponse>("/businesses", params),
  });

export const useBusinessMapLocations = (
  params: BusinessMapLocationParams = {},
) =>
  useQuery<BusinessMapLocationsResponse>({
    queryKey: businessKeys.mapLocations(params),
    queryFn: () =>
      get<BusinessMapLocationsResponse>("/businesses/map-locations", params),
  });

export const useBusiness = (
  id: string | undefined,
  options?: { enabled?: boolean },
) =>
  useQuery<BusinessEntity>({
    queryKey: businessKeys.detail(id ?? ""),
    queryFn: () => get<BusinessEntity>(`/businesses/${id}`),
    enabled: (options?.enabled ?? true) && !!id,
  });

export const useCities = () =>
  useQuery<string[]>({
    queryKey: businessKeys.cities(),
    queryFn: () => get<string[]>("/businesses/cities"),
    staleTime: 5 * 60 * 1000,
  });

export const useBusinessActivity = (
  id: string | undefined,
  params: { page?: number; limit?: number; docId?: string } = {},
) =>
  useQuery<ActivityListResponse>({
    queryKey: [...businessKeys.activity(id ?? ""), params],
    queryFn: () =>
      get<ActivityListResponse>(`/businesses/${id}/activity`, params),
    enabled: !!id,
  });

export interface CreateBusinessInput {
  ownerId?: string;
  owner?: { name: string; phone: string; email?: string; whatsapp?: string };
  businessName: string;
  nameEn: string;
  nameMl: string;
  descriptionEn?: string;
  descriptionMl?: string;
  phone: string;
  email?: string;
  whatsapp?: string;
  address: string;
  city: string;
  district?: string;
  state?: string;
  pincode?: string;
  lat?: number;
  lng?: number;
  careTypes?: string[];
  totalBeds?: number;
  staffLanguages?: string[];
  amenityIds?: string[];
  bankAccount?: BankAccount;
  upiId?: string;
  gstNumber?: string;
  panNumber?: string;
  tradeLicenseNumber?: string;
  fssaiNumber?: string;
  shopEstablishmentNumber?: string;
  isMedicalProfessional?: boolean;
  medicalRegistrationNo?: string;
  medicalCouncil?: string;
  medicalQualifications?: string;
}

export interface DocumentUploadMeta {
  docType: string;
  label?: string;
  docNumber?: string;
  issuedAt?: string;
  expiryDate?: string;
}

export const useCreateBusiness = () => {
  const qc = useQueryClient();
  return useMutation<BusinessEntity, Error, CreateBusinessInput>({
    mutationFn: (data) => post<BusinessEntity>("/businesses", data),
    onSuccess: () => qc.invalidateQueries({ queryKey: businessKeys.all }),
  });
};

export const useUpdateBusiness = () => {
  const qc = useQueryClient();
  return useMutation<
    BusinessEntity,
    Error,
    { id: string; data: Partial<BusinessEntity> }
  >({
    mutationFn: ({ id, data }) =>
      put<BusinessEntity>(`/businesses/${id}`, data),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: businessKeys.detail(v.id) });
      qc.invalidateQueries({ queryKey: businessKeys.all });
    },
  });
};

export const useUpdateBusinessCredentials = () => {
  const qc = useQueryClient();
  return useMutation<
    BusinessEntity,
    Error,
    { id: string; data: Partial<BusinessEntity> }
  >({
    mutationFn: ({ id, data }) =>
      apiPatch<BusinessEntity>(`/businesses/${id}/credentials`, data),
    onSuccess: (_d, v) =>
      qc.invalidateQueries({ queryKey: businessKeys.detail(v.id) }),
  });
};

export const useVerifyBusiness = () => {
  const qc = useQueryClient();
  return useMutation<
    BusinessEntity,
    Error,
    {
      id: string;
      status: BusinessStatus;
      reasons?: string[];
      note?: string;
      rejectionReason?: string;
    }
  >({
    mutationFn: ({ id, status, reasons, note, rejectionReason }) =>
      post<BusinessEntity>(`/businesses/${id}/verify`, {
        status,
        reasons,
        note,
        rejectionReason,
      }),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: businessKeys.all });
      qc.invalidateQueries({ queryKey: businessKeys.detail(v.id) });
      qc.invalidateQueries({ queryKey: businessKeys.activity(v.id) });
    },
  });
};

export const useToggleBusiness = () => {
  const qc = useQueryClient();
  return useMutation<BusinessEntity, Error, { id: string; isVisible: boolean }>(
    {
      mutationFn: ({ id, isVisible }) =>
        post<BusinessEntity>(`/businesses/${id}/toggle`, { isVisible }),
      onSuccess: (_d, v) => {
        qc.invalidateQueries({ queryKey: businessKeys.all });
        qc.invalidateQueries({ queryKey: businessKeys.detail(v.id) });
      },
    },
  );
};

export const useToggleBusinessFeatured = () => {
  const qc = useQueryClient();
  return useMutation<
    BusinessEntity,
    Error,
    { id: string; isFeatured: boolean }
  >({
    mutationFn: ({ id, isFeatured }) =>
      post<BusinessEntity>(`/businesses/${id}/toggle-featured`, { isFeatured }),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: businessKeys.all });
      qc.invalidateQueries({ queryKey: businessKeys.detail(v.id) });
    },
  });
};

export const useToggleBranchesVisibility = () => {
  const qc = useQueryClient();
  return useMutation<
    BusinessEntity,
    Error,
    { id: string; showBranchesInCustomerApp: boolean }
  >({
    mutationFn: ({ id, showBranchesInCustomerApp }) =>
      post<BusinessEntity>(`/businesses/${id}/toggle-branches`, {
        showBranchesInCustomerApp,
      }),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: businessKeys.all });
      qc.invalidateQueries({ queryKey: businessKeys.detail(v.id) });
    },
  });
};

export const useDeleteBusiness = () => {
  const qc = useQueryClient();
  return useMutation<unknown, Error, string>({
    mutationFn: (id) => del(`/businesses/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: businessKeys.all }),
  });
};

export const useBulkDeleteBusinesses = () => {
  const qc = useQueryClient();
  return useMutation<unknown, Error, string[]>({
    mutationFn: async (ids: string[]) => {
      await Promise.all(ids.map((id) => del(`/businesses/${id}`)));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: businessKeys.all }),
  });
};

export const useUploadBusinessDocuments = () => {
  const qc = useQueryClient();
  return useMutation<
    BusinessDocumentEntity[],
    Error,
    { id: string; files: File[]; metadata: DocumentUploadMeta[] }
  >({
    mutationFn: ({ id, files, metadata }) => {
      const form = new FormData();
      files.forEach((f) => form.append("files", f));
      form.append("metadata", JSON.stringify(metadata));
      return postForm<BusinessDocumentEntity[]>(
        `/businesses/${id}/documents`,
        form,
      );
    },
    onSuccess: (_d, v) =>
      qc.invalidateQueries({ queryKey: businessKeys.detail(v.id) }),
  });
};

export const useDeleteBusinessDocument = () => {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { id: string; docId: string }>({
    mutationFn: ({ id, docId }) => del(`/businesses/${id}/documents/${docId}`),
    onSuccess: (_d, v) =>
      qc.invalidateQueries({ queryKey: businessKeys.detail(v.id) }),
  });
};

export interface BusinessPhotoEntity {
  id: string;
  url: string;
  sortOrder: number;
  isCover: boolean;
  uploadedAt: string;
}

export const useUploadBusinessPhotos = () => {
  const qc = useQueryClient();
  return useMutation<
    BusinessPhotoEntity[],
    Error,
    { id: string; files: File[] }
  >({
    mutationFn: ({ id, files }) => {
      const form = new FormData();
      files.forEach((f) => form.append("files", f));
      return postForm<BusinessPhotoEntity[]>(`/businesses/${id}/photos`, form);
    },
    onSuccess: (_d, v) =>
      qc.invalidateQueries({ queryKey: businessKeys.detail(v.id) }),
  });
};

export const useUploadBusinessLogo = () => {
  const qc = useQueryClient();
  return useMutation<{ logoUrl: string }, Error, { id: string; file: File }>({
    mutationFn: ({ id, file }) => {
      const form = new FormData();
      form.append("file", file);
      return postForm<{ logoUrl: string }>(`/businesses/${id}/logo`, form);
    },
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: businessKeys.detail(v.id) });
      qc.invalidateQueries({ queryKey: businessKeys.all });
    },
  });
};

export const useDeleteBusinessPhoto = () => {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { id: string; photoId: string }>({
    mutationFn: ({ id, photoId }) => del(`/businesses/${id}/photos/${photoId}`),
    onSuccess: (_d, v) =>
      qc.invalidateQueries({ queryKey: businessKeys.detail(v.id) }),
  });
};

export const useSetCoverPhoto = () => {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { id: string; photoId: string }>({
    mutationFn: ({ id, photoId }) =>
      apiPatch(`/businesses/${id}/photos/${photoId}/cover`, {}),
    onSuccess: (_d, v) =>
      qc.invalidateQueries({ queryKey: businessKeys.detail(v.id) }),
  });
};

export const useReorderBusinessPhotos = () => {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { id: string; order: string[] }>({
    mutationFn: ({ id, order }) =>
      apiPatch(`/businesses/${id}/photos/reorder`, { order }),
    onSuccess: (_d, v) =>
      qc.invalidateQueries({ queryKey: businessKeys.detail(v.id) }),
  });
};

export const useMarkDocumentViewed = () => {
  const qc = useQueryClient();
  return useMutation<
    BusinessDocumentEntity,
    Error,
    { id: string; docId: string }
  >({
    mutationFn: ({ id, docId }) =>
      post<BusinessDocumentEntity>(
        `/businesses/${id}/documents/${docId}/view`,
        {},
      ),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: businessKeys.detail(v.id) });
      qc.invalidateQueries({ queryKey: businessKeys.activity(v.id) });
    },
  });
};

export const useRejectDocument = () => {
  const qc = useQueryClient();
  return useMutation<
    BusinessDocumentEntity,
    Error,
    { id: string; docId: string; note?: string }
  >({
    mutationFn: ({ id, docId, note }) =>
      apiPatch<BusinessDocumentEntity>(
        `/businesses/${id}/documents/${docId}/reject`,
        { note },
      ),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: businessKeys.detail(v.id) });
      qc.invalidateQueries({ queryKey: businessKeys.activity(v.id) });
    },
  });
};

export interface AmenityMasterEntity {
  id: string;
  name: string;
  nameMalayalam: string | null;
  category?: AmenityCategory;
  iconKey: string;
  sortOrder: number;
  isActive: boolean;
  isPremium: boolean;
}

export const useAmenityMasterList = (opts?: { includeInactive?: boolean }) => {
  const includeInactive = !!opts?.includeInactive;
  return useQuery<AmenityMasterEntity[]>({
    queryKey: ["amenities-master", { includeInactive }],
    queryFn: () =>
      get<AmenityMasterEntity[]>(
        `/businesses/amenities${includeInactive ? "?includeInactive=true" : ""}`,
      ),
    staleTime: 30 * 60 * 1000,
  });
};

export interface CreateAmenityInput {
  name: string;
  nameMalayalam?: string;
  category?: AmenityCategory;
  iconKey?: string;
  sortOrder?: number;
  isPremium?: boolean;
}

export const useCreateAmenity = () => {
  const qc = useQueryClient();
  return useMutation<AmenityMasterEntity, Error, CreateAmenityInput>({
    mutationFn: (data) =>
      post<AmenityMasterEntity>("/businesses/amenities", data),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["amenities-master"], exact: false }),
  });
};

export const useUpdateAmenity = () => {
  const qc = useQueryClient();
  return useMutation<
    AmenityMasterEntity,
    Error,
    { id: string; data: Partial<CreateAmenityInput> & { isActive?: boolean } }
  >({
    mutationFn: ({ id, data }) =>
      put<AmenityMasterEntity>(`/businesses/amenities/${id}`, data),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["amenities-master"], exact: false }),
  });
};

export interface DeleteAmenityResult {
  id: string;
  deleted: "soft" | "hard";
  referenceCount: number;
}

export const useDeleteAmenity = () => {
  const qc = useQueryClient();
  return useMutation<DeleteAmenityResult, Error, string>({
    mutationFn: (id) => del<DeleteAmenityResult>(`/businesses/amenities/${id}`),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["amenities-master"], exact: false }),
  });
};
